import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { ArrowDownCircle, ArrowUpCircle, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { formatMetaInsightsValue } from '@/utils/currency';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';
import { stockService, type StockItem, type StockMovement, type StockMovementType } from '@/services/stock.service';

const TYPE_META: Record<StockMovementType, { label: string; icon: React.ReactNode; className: string }> = {
  in: { label: 'Stock in', icon: <ArrowUpCircle size={13} />, className: 'bg-success-soft text-success' },
  out: { label: 'Stock out', icon: <ArrowDownCircle size={13} />, className: 'bg-info-soft text-info' },
  waste: { label: 'Waste', icon: <Trash2 size={13} />, className: 'bg-danger-soft text-danger' },
  adjust: { label: 'Adjust', icon: <RotateCcw size={13} />, className: 'bg-surface-alt text-text-muted' },
};

interface Props {
  open: boolean;
  stockUid: string | null;
  onClose: () => void;
  onEdit: (item: StockItem) => void;
  onRecordMovement: (item: StockItem) => void;
}

export default function StockItemDetailModal({ open, stockUid, onClose, onEdit, onRecordMovement }: Props) {
  const companyCurrency = useCompanyCurrency();
  const [item, setItem] = useState<StockItem | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [usage7, setUsage7] = useState(0);
  const [usage30, setUsage30] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!stockUid) return;
    setLoading(true);
    try {
      const r = await stockService.getItem(stockUid);
      setItem(r.item); setMovements(r.movements); setUsage7(r.usage7Days); setUsage30(r.usage30Days);
    } catch { /* interceptor toasts */ } finally { setLoading(false); }
  }, [stockUid]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const currency = item?.currency ?? companyCurrency;

  return (
    <Modal open={open} onClose={onClose} title={item ? item.name : 'Stock item'} widthClass="max-w-xl">
      {loading || !item ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-12 rounded-lg" />)}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MiniStat label="On hand" value={`${item.quantity} ${item.unit}`} highlight={item.low} />
            <MiniStat label="Reorder at" value={`${item.reorderLevel} ${item.unit}`} />
            <MiniStat label="Value" value={formatMetaInsightsValue(String(item.value), currency)} />
            <MiniStat label="Used (7d)" value={`${usage7} ${item.unit}`} />
          </div>

          <div className="flex flex-wrap gap-2 text-2xs text-text-muted">
            {item.sku && <span className="rounded-full bg-surface-alt px-2 py-0.5">SKU {item.sku}</span>}
            {item.category && <span className="rounded-full bg-surface-alt px-2 py-0.5">{item.category}</span>}
            {item.supplier && <span className="rounded-full bg-surface-alt px-2 py-0.5">{item.supplier}</span>}
            <span className="rounded-full bg-surface-alt px-2 py-0.5">{formatMetaInsightsValue(String(item.costPerUnit), currency)}/{item.unit}</span>
            <span className="rounded-full bg-surface-alt px-2 py-0.5">Used (30d): {usage30} {item.unit}</span>
            {!item.isActive && <span className="rounded-full bg-danger-soft px-2 py-0.5 text-danger">Inactive</span>}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" iconLeft={<Plus size={13} />} onClick={() => onRecordMovement(item)}>Record movement</Button>
            <Button size="sm" variant="secondary" onClick={() => onEdit(item)}>Edit item</Button>
          </div>

          <div>
            <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-text-muted">Movement history</div>
            {movements.length === 0 ? (
              <p className="text-xs text-text-muted">No movements recorded yet.</p>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
                {movements.map((m) => (
                  <div key={m.movementUid} className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-xs">
                    <span className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', TYPE_META[m.type].className)}>
                      {TYPE_META[m.type].icon}{TYPE_META[m.type].label}
                    </span>
                    <span className="font-medium text-text-primary">
                      {m.type === 'adjust' ? `→ ${m.quantity}` : `${m.type === 'in' ? '+' : '−'}${m.quantity}`} {item.unit}
                    </span>
                    <span className="text-text-muted">now {m.quantityAfter} {item.unit}</span>
                    {m.note && <span className="min-w-0 flex-1 truncate text-text-muted" title={m.note}>· {m.note}</span>}
                    <span className="ml-auto shrink-0 text-[10px] text-text-muted">{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function MiniStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={clsx('rounded-xl border border-border p-2.5', highlight && 'border-danger/40 bg-danger-soft/40')}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">{label}</div>
      <div className={clsx('mt-0.5 text-sm font-semibold', highlight ? 'text-danger' : 'text-text-primary')}>{value}</div>
    </div>
  );
}
